import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Edit, FileText, Package, User, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Breadcrumbs, DetailList, Panel } from '@/components/ui/detail-list'
import { AssignmentModal } from '@/components/modals/AssignmentModal'
import { DeviceStatusModal } from '@/components/modals/DeviceStatusModal'
import { AssignmentApprovalModal } from '@/components/modals/AssignmentApprovalModal'
import { DeviceForm } from '@/components/forms/DeviceForm'
import { DeviceModelForm } from '@/components/forms/DeviceModelForm'
import { generateAssignmentPDF } from '@/utils/pdf-generator'
import { apiClient } from '@/lib/api-client'
import {
  canAssign,
  canReturn,
  returnAsset,
  ownershipLabel,
  powerDetail,
  powerFieldLabel,
  statusLabel,
  typeLabel,
} from '@/lib/device-format'
import { useAuth } from '@/hooks/useAuth'
import { formatDateShort, getStatusColor } from '@shared/utils'
import { toast } from 'react-hot-toast'

function assignmentBadge(status: string) {
  switch (status) {
    case 'approved':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300'
    case 'pending':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300'
    case 'rejected':
      return 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-300'
    default:
      return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
  }
}

export function DeviceDetailsPage() {
  const { id } = useParams()
  const { user } = useAuth()
  const isAdmin = user?.role === 'Admin'
  const queryClient = useQueryClient()
  const [assignOpen, setAssignOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editModel, setEditModel] = useState(false)
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null)
  const [statusOpen, setStatusOpen] = useState(false)
  const [returning, setReturning] = useState(false)

  const { data: device, isLoading } = useQuery({
    queryKey: ['device', id],
    queryFn: async () => (await apiClient.get(`/devices/${id}`)).data,
    enabled: !!id,
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['device', id] })
    queryClient.invalidateQueries({ queryKey: ['devices'] })
    queryClient.invalidateQueries({ queryKey: ['assignments'] })
    queryClient.invalidateQueries({ queryKey: ['device-model'] })
    queryClient.invalidateQueries({ queryKey: ['device-models'] })
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!device) {
    return (
      <div className="py-16 text-center">
        <Package className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
        <p className="text-sm font-medium">Asset not found</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/devices">Back to devices</Link>
        </Button>
      </div>
    )
  }

  const model = device.deviceModel
  const serial = device.serialNumber || device.name
  const history = [...(device.assignments || [])].sort(
    (a: any, b: any) => new Date(b.assignedOn).getTime() - new Date(a.assignedOn).getTime(),
  )
  const currentAssignment = history.find((item: any) => !item.returnedOn && item.status !== 'rejected')

  const crumbs: { label: string; to?: string }[] = [{ label: 'Devices', to: '/devices' }]
  const markReturned = async () => {
    if (!window.confirm(`Mark ${serial} as returned? It will be available to assign again.`)) return
    setReturning(true)
    try {
      await returnAsset(device.id)
      toast.success('Asset marked returned')
      refresh()
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Could not mark the asset returned')
    } finally {
      setReturning(false)
    }
  }

  if (model) crumbs.push({ label: model.name, to: `/devices/models/${model.id}` })
  crumbs.push({ label: serial })

  return (
    <div className="space-y-4">
      <Breadcrumbs items={crumbs} />

      <div className="flex flex-col gap-3 border-b border-border/70 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="truncate font-mono text-xl font-semibold tracking-tight">{serial}</h1>
          <Badge className={getStatusColor(device.status)}>{statusLabel(device.status)}</Badge>
        </div>
        {isAdmin && (
          <div className="grid grid-cols-2 gap-2 max-sm:[&_button]:w-full sm:flex">
            {model && (
              <Button variant="outline" onClick={() => setEditModel(true)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit model
              </Button>
            )}
            <Button variant="outline" onClick={() => setEditOpen(true)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit asset
            </Button>
            <Button variant="outline" onClick={() => setStatusOpen(true)}>
              Update status
            </Button>
            {canReturn(device.status) && (
              <Button variant="outline" disabled={returning} onClick={() => markReturned()}>
                Mark returned
              </Button>
            )}
            {canAssign(device.status) && (
              <Button onClick={() => setAssignOpen(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                {currentAssignment ? 'Reassign' : 'Assign'}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Asset details" className="lg:col-span-2">
          <div className="grid md:grid-cols-2 md:divide-x md:divide-border/70">
            <DetailList
              items={[
                { label: 'Serial number', value: device.serialNumber, mono: true },
                {
                  label: 'Model',
                  value: model ? (
                    <Link to={`/devices/models/${model.id}`} className="text-primary hover:underline">
                      {model.name}
                    </Link>
                  ) : device.name,
                },
                { label: 'Brand', value: model?.brand },
                { label: 'Model number', value: model?.modelNumber, mono: true },
              ]}
            />
            <DetailList
              items={[
                { label: 'Power', value: typeLabel(device) },
                {
                  label: powerFieldLabel(device),
                  value: powerDetail(device),
                },
                ...(device.deviceType === 'chargeable' && device.chargerAvailable
                  ? [{ label: 'Charger ID', value: device.chargerId, mono: true }]
                  : []),
                { label: 'Ownership', value: ownershipLabel(device) },
                ...(device.statusReason
                  ? [
                      { label: 'Status reason', value: device.statusReason },
                      {
                        label: 'Status changed',
                        value: device.statusChangedAt ? formatDateShort(device.statusChangedAt) : null,
                      },
                    ]
                  : []),
                { label: 'Added', value: `${formatDateShort(device.createdAt)}${device.creator?.name ? ` · ${device.creator.name}` : ''}` },
              ]}
            />
          </div>
        </Panel>

        <Panel title="Current holder" className="self-start">
          {currentAssignment ? (
            <div className="space-y-3 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                  {(currentAssignment.employee?.name || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{currentAssignment.employee?.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{currentAssignment.employee?.email}</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Since {formatDateShort(currentAssignment.assignedOn)}</span>
                <Badge className={assignmentBadge(currentAssignment.status)}>
                  {currentAssignment.status === 'pending' ? 'Awaiting approval' : currentAssignment.status}
                </Badge>
              </div>
              {currentAssignment.notes && (
                <p className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">{currentAssignment.notes}</p>
              )}
              {currentAssignment.status === 'pending' && currentAssignment.employeeId === user?.id && (
                <Button size="sm" className="w-full" onClick={() => setSelectedAssignment(currentAssignment)}>
                  Review & approve
                </Button>
              )}
              {isAdmin && canReturn(device.status) && (
                <Button size="sm" variant="outline" className="w-full" disabled={returning} onClick={() => markReturned()}>
                  Mark returned
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
              <User className="h-8 w-8 text-muted-foreground/60" />
              <p className="text-sm text-muted-foreground">Not assigned</p>
              {isAdmin && device.status === 'available' && (
                <Button size="sm" variant="outline" onClick={() => setAssignOpen(true)}>
                  Assign
                </Button>
              )}
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Assignment history">
        {history.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">No assignments yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Assigned</th>
                  <th>Approved</th>
                  <th>Returned</th>
                  <th>Status</th>
                  <th className="text-right">Document</th>
                </tr>
              </thead>
              <tbody>
                {history.map((assignment: any) => (
                  <tr key={assignment.id}>
                    <td>
                      <p className="text-sm font-medium">{assignment.employee?.name}</p>
                      <p className="text-xs text-muted-foreground">{assignment.employee?.email}</p>
                    </td>
                    <td data-label="Assigned">{formatDateShort(assignment.assignedOn)}</td>
                    <td data-label="Approved" className="text-muted-foreground">{assignment.approvedOn ? formatDateShort(assignment.approvedOn) : '—'}</td>
                    <td data-label="Returned" className="text-muted-foreground">{assignment.returnedOn ? formatDateShort(assignment.returnedOn) : '—'}</td>
                    <td data-label="Status">
                      <Badge className={assignmentBadge(assignment.returnedOn ? 'returned' : assignment.status)}>
                        {assignment.returnedOn ? 'returned' : assignment.status}
                      </Badge>
                    </td>
                    <td data-label="Document" className="is-actions text-right">
                      {assignment.status === 'approved' ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8"
                          onClick={() => generateAssignmentPDF({
                            ...assignment,
                            device: {
                              id: device.id,
                              name: model ? `${model.name} (${serial})` : device.name,
                              description: device.description,
                              deviceType: device.deviceType,
                              chargerId: device.chargerId,
                              batteryType: device.batteryType,
                              batteryCount: device.batteryCount,
                            },
                          })}
                        >
                          <FileText className="mr-1.5 h-3.5 w-3.5" />
                          PDF
                        </Button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <DeviceStatusModal
        isOpen={statusOpen}
        onClose={() => setStatusOpen(false)}
        deviceId={device.id}
        serial={serial}
        currentStatus={device.status}
        hasHolder={!!currentAssignment}
        onSuccess={refresh}
      />
      <AssignmentModal
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        deviceId={device.id}
        deviceName={model ? `${model.name} · ${serial}` : serial}
        currentAssignment={currentAssignment}
        onSuccess={refresh}
      />
      <DeviceModelForm
        isOpen={editModel}
        modelId={editModel ? model?.id ?? null : null}
        onClose={() => setEditModel(false)}
        onSuccess={refresh}
      />
      <DeviceForm
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        device={device}
        onSuccess={refresh}
      />
      {selectedAssignment && (
        <AssignmentApprovalModal
          isOpen={!!selectedAssignment}
          onClose={() => setSelectedAssignment(null)}
          assignment={selectedAssignment}
          onSuccess={refresh}
        />
      )}
    </div>
  )
}
